// Recadrage de la couverture d'un projet, avant l'envoi.
//
// Le client voit toujours la couverture en 4/3, rognée par le centre (`object-fit: cover` sur la carte du
// projet et sur l'en-tête). Sans recadrage, c'est le navigateur qui décidait seul de ce qu'il coupait.
// Ici le studio cadre lui-même, dans un cadre au format exact du client : ce qu'il voit est ce qui part.
//
// Le fichier envoyé est déjà recadré : la chaîne d'envoi (logeUploads) n'a pas à connaître de rectangle.
import { formDialog } from './logeDialogs';
import { el } from './dom';

/** Format sous lequel le client affiche la couverture (carte du projet et en-tête). */
export const RATIO = 4 / 3;
/** Côté le plus long de l'image produite : au-delà, on n'y gagne qu'un fichier plus lourd. */
const OUT_W = 1600;
/** Agrandissement maximal, pour ne pas laisser monter jusqu'au flou. */
const MAX_ZOOM = 4;

export interface Frame { w: number; h: number }

/** Position de l'image dans le cadre : échelle, et décalage depuis le centre. */
export interface View { scale: number; tx: number; ty: number }

/** Échelle minimale : celle qui couvre le cadre sans laisser de vide. */
export const coverScale = (iw: number, ih: number, f: Frame): number => Math.max(f.w / iw, f.h / ih);

/** Le cadre doit toujours rester couvert : on borne le décalage à ce que l'image dépasse. */
export function clamp(v: View, iw: number, ih: number, f: Frame): View {
  const maxX = Math.max(0, (iw * v.scale - f.w) / 2);
  const maxY = Math.max(0, (ih * v.scale - f.h) / 2);
  return { scale: v.scale, tx: Math.min(maxX, Math.max(-maxX, v.tx)), ty: Math.min(maxY, Math.max(-maxY, v.ty)) };
}

/** Rectangle de l'image d'origine qui se retrouve dans le cadre. */
export function sourceRect(v: View, iw: number, ih: number, f: Frame): { sx: number; sy: number; sw: number; sh: number } {
  const sw = f.w / v.scale;
  const sh = f.h / v.scale;
  return { sx: (iw - sw) / 2 - v.tx / v.scale, sy: (ih - sh) / 2 - v.ty / v.scale, sw, sh };
}

async function cropToFile(img: HTMLImageElement, rect: { sx: number; sy: number; sw: number; sh: number }, name: string): Promise<File> {
  const c = document.createElement('canvas');
  c.width = OUT_W;
  c.height = Math.round(OUT_W / RATIO);
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Recadrage impossible sur cet appareil.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, c.width, c.height);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Recadrage impossible sur cet appareil.');
  return new File([blob], name.replace(/\.[a-z0-9]+$/i, '') + '.jpg', { type: 'image/jpeg' });
}

/**
 * Ouvre le recadrage et rend une image 4/3 prête à envoyer. La fenêtre ne se ferme qu'une fois l'image
 * produite : si le navigateur n'y arrive pas, le message reste affiché plutôt que de laisser partir
 * la photo non recadrée.
 */
export function openCoverCrop(file: File, onDone: (cropped: File) => void): void {
  const url = URL.createObjectURL(file);
  const img = new Image();
  let view: View = { scale: 1, tx: 0, ty: 0 };
  let frame: Frame = { w: 1, h: 1 };

  formDialog({
    title: 'Cadrer la couverture',
    sub: 'Déplacez l’image et ajustez la taille. Le client la verra exactement ainsi.',
    body: `<div class="loge-crop">
        <div class="loge-crop-stage" data-stage><img alt="" data-img draggable="false"></div>
        <label class="loge-crop-zoom"><span>Taille</span>
          <input type="range" data-zoom min="100" max="${MAX_ZOOM * 100}" value="100" step="1" aria-label="Agrandir l’image"></label>
        <p class="hint">Format 4/3, celui de la carte du projet et de l’en-tête chez le client.</p>
      </div>`,
    submit: 'Utiliser ce cadrage',
    onOpen: (d) => {
      const stage = el<HTMLElement>(d, '[data-stage]');
      const pic = el<HTMLImageElement>(d, '[data-img]');
      const zoom = el<HTMLInputElement>(d, '[data-zoom]');

      const apply = (): void => {
        view = clamp(view, img.naturalWidth, img.naturalHeight, frame);
        pic.style.width = `${img.naturalWidth * view.scale}px`;
        pic.style.height = `${img.naturalHeight * view.scale}px`;
        pic.style.transform = `translate(calc(-50% + ${view.tx}px), calc(-50% + ${view.ty}px))`;
      };

      img.onload = () => {
        frame = { w: stage.clientWidth, h: stage.clientWidth / RATIO };
        const base = coverScale(img.naturalWidth, img.naturalHeight, frame);
        view = { scale: base, tx: 0, ty: 0 };
        pic.src = url;
        apply();
        zoom.oninput = () => {
          view = { ...view, scale: base * (Number(zoom.value) / 100) };
          apply();
        };
      };
      img.src = url;

      // Déplacement à la souris comme au doigt : un seul jeu d'événements.
      let from: { x: number; y: number; tx: number; ty: number } | null = null;
      stage.addEventListener('pointerdown', (e) => {
        from = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
        stage.setPointerCapture(e.pointerId);
        stage.classList.add('dragging');
      });
      stage.addEventListener('pointermove', (e) => {
        if (!from) return;
        view = { ...view, tx: from.tx + (e.clientX - from.x), ty: from.ty + (e.clientY - from.y) };
        apply();
      });
      const stop = (): void => { from = null; stage.classList.remove('dragging'); };
      stage.addEventListener('pointerup', stop);
      stage.addEventListener('pointercancel', stop);
    },
    onSubmit: async () => {
      if (!img.naturalWidth) return 'L’image n’a pas pu être lue.';
      const cropped = await cropToFile(img, sourceRect(view, img.naturalWidth, img.naturalHeight, frame), file.name);
      URL.revokeObjectURL(url);
      onDone(cropped);
    },
  }).addEventListener('close', () => URL.revokeObjectURL(url));
}
